import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate, formatTime } from '@/lib/utils';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';

export function BookingConfirmationPage() {
  const { confirmationCode } = useParams<{ confirmationCode: string }>();

  const { data: booking, isLoading, error } = useQuery({
    queryKey: ['booking', 'confirmation', confirmationCode],
    queryFn: () => api.bookings.getByConfirmation(confirmationCode!),
    enabled: !!confirmationCode,
  });

  if (isLoading) {
    return (
      <div className="container-narrow py-16">
        <Skeleton className="h-12 w-64 mx-auto mb-8" />
        <Card>
          <Skeleton className="h-64" />
        </Card>
      </div>
    );
  }

  if (error || !booking?.data) {
    return (
      <div className="container-narrow py-16 text-center">
        <h1 className="text-2xl font-bold text-sand-900">Booking not found</h1>
        <p className="mt-4 text-sand-500">We couldn&apos;t find a booking with that confirmation code.</p>
        <Link to="/bookings" className="mt-8 inline-block">
          <Button>View My Bookings</Button>
        </Link>
      </div>
    );
  }

  const { data } = booking;

  return (
    <div className="bg-sand-50 min-h-screen py-16">
      <div className="container-narrow">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage-100 mb-4">
            <svg className="h-8 w-8 text-sage-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-sand-900">Booking Confirmed!</h1>
          <p className="mt-2 text-sand-500">We&apos;ve sent a confirmation email with all the details.</p>
        </div>

        <Card>
          {/* Confirmation Code */}
          <div className="text-center py-6 border-b border-sand-100">
            <p className="text-sm text-sand-500 mb-1">Confirmation Code</p>
            <p className="text-3xl font-bold tracking-wider text-sand-900">{data.confirmationCode}</p>
          </div>

          {/* Tee Time Details */}
          <div className="py-6 border-b border-sand-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-sand-900">{data.course?.name}</h2>
                <p className="text-sand-500">{data.course?.address}</p>
                <p className="text-sand-500">{data.course?.city}, {data.course?.state}</p>
              </div>
              <Badge variant="sage">{data.status}</Badge>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl bg-sand-50 p-4">
                <p className="text-sm text-sand-500">Date</p>
                <p className="text-lg font-semibold text-sand-900">{formatDate(data.teeTime?.date)}</p>
              </div>
              <div className="rounded-xl bg-sand-50 p-4">
                <p className="text-sm text-sand-500">Time</p>
                <p className="text-lg font-semibold text-sand-900">{formatTime(data.teeTime?.time)}</p>
              </div>
              <div className="rounded-xl bg-sand-50 p-4">
                <p className="text-sm text-sand-500">Players</p>
                <p className="text-lg font-semibold text-sand-900">{data.playerCount}</p>
              </div>
            </div>
          </div>

          {/* Players */}
          {data.players && data.players.length > 0 && (
            <div className="py-6 border-b border-sand-100">
              <h3 className="font-semibold text-sand-900 mb-4">Players</h3>
              <div className="space-y-2">
                {data.players.map((player: any, index: number) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sage-100 text-sm font-medium text-sage-700">
                      {player.firstName.charAt(0)}{player.lastName.charAt(0)}
                    </div>
                    <span className="text-sand-700">{player.firstName} {player.lastName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total */}
          <div className="py-6">
            <div className="flex justify-between text-lg font-semibold text-sand-900">
              <span>Total Paid</span>
              <span>{formatCurrency(data.totalAmount)}</span>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={`/bookings/${data.id}`}>
            <Button variant="outline" className="w-full sm:w-auto">
              View Booking Details
            </Button>
          </Link>
          <Link to="/tee-times">
            <Button variant="sage" className="w-full sm:w-auto">
              Book Another Tee Time
            </Button>
          </Link>
        </div>

        {/* What's Next */}
        <Card className="mt-8">
          <h3 className="font-semibold text-sand-900 mb-4">What&apos;s Next?</h3>
          <ul className="space-y-3 text-sm text-sand-600">
            <li className="flex items-start gap-3">
              <svg className="h-5 w-5 text-sage-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Check your email for a confirmation with all booking details</span>
            </li>
            <li className="flex items-start gap-3">
              <svg className="h-5 w-5 text-sage-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Arrive at least 15 minutes before your tee time</span>
            </li>
            <li className="flex items-start gap-3">
              <svg className="h-5 w-5 text-sage-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>Bring your confirmation code to check in at the pro shop</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
